import type { StaticEntityType } from "@config";
import { useViewContext } from "@editor-providers";
import { PdfLibVideoWrapper } from "@library/components/pdf";
import { VideoType } from "@library/types";
import VidyardEmbed from "@vidyard/embed-code";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
} from "react";

import { cn } from "@/lib/cn";
import {
  getMobileMediaCapVars,
  useMediaLazyLoad,
  validateDynamicVariable,
} from "@/shared";

import {
  StyledVideoElement,
  StyledVideoLayout,
  StyledVideoWrapper,
} from "../styles/Video.styled";
import "../styles/index.scss";
import { SandboxedHtml } from "../../embedded/components/SandboxedHtml";
import type { EditorDataProps, EditorSlotsProps, VideoProps } from "../types";
import {
  getLoomEmbedSrc,
  getVidyardVideoId,
  getVimeoEmbedSrc,
  getYoutubeEmbedSrc,
} from "../utils";
import { Preview } from "./Preview";

const enum VideoRenderType {
  PREVIEW = "preview",
  LIBRARY_VIDEO = "libraryVideo",
  YOUTUBE = "youtube",
  VIMEO = "vimeo",
  LOOM = "loom",
  VIDYARD = "vidyard",
  CODE = "code",
  NONE = "none",
}

const Source = ({
  url,
  trimStart,
  trimEnd,
}: { url: string; trimStart?: number; trimEnd?: number }) => {
  const fragment =
    trimStart != null && trimEnd != null
      ? `#t=${trimStart},${trimEnd}`
      : "#t=0.1";

  if (url.endsWith(".webm")) {
    const mp4Url = url.replace(/\.webm$/, ".mp4");
    return (
      <>
        <source src={`${url}${fragment}`} type="video/webm" />
        <source src={`${mp4Url}${fragment}`} type="video/mp4" />
      </>
    );
  }
  return <source src={`${url}${fragment}`} type="video/mp4" />;
};

export const VideoLibrary = ({
  entity,
  editorSlots,
  editorData,
}: {
  entity: StaticEntityType;
  editorSlots?: EditorSlotsProps;
  editorData?: EditorDataProps;
}) => {
  const { view } = useViewContext();
  const { setObserverRef, isVisible } = useMediaLazyLoad();
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const { settings } = entity as StaticEntityType<VideoProps>;

  const isPdf = view === "pdf";
  const isEditor = view === "editor";
  const DND_ATTR = isEditor ? { "data-dnd-trigger": "true" } : {};
  const isFreeShape = settings.shape === "free" || !settings.shape;

  const width = editorData?.width || settings.width;
  const height = editorData?.height || settings.height;

  // ===== DYNAMIC VARIABLES =====
  const { hasDynamicVariable, dynamicVariableParsed } = useMemo(
    () => validateDynamicVariable(settings.dynamicVariableSrc),
    [settings.dynamicVariableSrc],
  );

  // ===== VIDEO CONTROLS =====
  const hasControls = useMemo(
    () => (isPdf ? false : settings.controls),
    [isPdf, settings.controls],
  );
  const shouldAutoPlay = useMemo(
    () => (isPdf ? false : settings.autoplay),
    [isPdf, settings.autoplay],
  );

  // ===== VIDEO SOURCE =====
  const videoSrc = useMemo(() => {
    let src = "";
    if (settings.type === "Library") {
      if (isPdf) {
        src = `${settings.src}#t=0.1`;
      } else if (isVisible) {
        src = settings.src;
        if (dynamicVariableParsed) {
          src = settings.dynamicVariableSrc as string;
        }
      }
    }
    return src;
  }, [
    settings.type,
    settings.src,
    settings.dynamicVariableSrc,
    isPdf,
    isVisible,
    dynamicVariableParsed,
  ]);

  // ===== ASPECT RATIO =====
  const metaAspectRatio = useMemo(() => {
    if (settings.isShort && isFreeShape) return undefined;
    if (settings.metadata && settings.metadata.width > 0 && isFreeShape) {
      return settings.metadata.height / settings.metadata.width;
    }
    return undefined;
  }, [settings.metadata, isFreeShape, settings.isShort]);

  // ===== WRAPPER STYLES =====
  const wrapperStyle = useMemo(() => {
    const widthNum = width != null ? Number(width) : 100;
    const isNarrowCombo = entity.layers?.combo?.settings?.maxWidth === "narrow";
    return {
      "--entity-size": `${widthNum}%`,
      ...getMobileMediaCapVars(width, settings.shape, isNarrowCombo),
    } as CSSProperties;
  }, [width, settings.shape, entity.layers?.combo?.settings?.maxWidth]);

  // ===== CONTAINER STYLES =====
  const containerStyle = useMemo(
    () => ({
      paddingTop: metaAspectRatio ? `${metaAspectRatio * 100}%` : undefined,
    }),
    [metaAspectRatio],
  );

  // ===== CROP / TRIM (editor-only fallback) =====
  // In the editor, crop/trim CSS fallback is provided via editorData.cropFallback
  // so the user sees their edits visually while processing runs.
  // In the static/published version, the processed video has crop/trim baked in.
  const cropFallback = editorData?.cropFallback;
  const hasTrim = cropFallback?.hasTrim ?? false;
  const cropContainerStyle = cropFallback?.cropContainerStyle;
  const cropVideoStyle = cropFallback?.cropVideoStyle;

  // ===== VIDEO KEY =====
  const cropData = settings.videoCropData;
  const trimData = settings.videoTrimData;
  const videoKey = useMemo(
    () =>
      videoSrc +
      shouldAutoPlay?.toString() +
      hasControls?.toString() +
      settings.loop?.toString() +
      settings.shape?.toString() +
      (cropData?.coordinates?.left ?? "") +
      (cropData?.coordinates?.top ?? "") +
      (cropData?.coordinates?.width ?? "") +
      (cropData?.coordinates?.height ?? "") +
      (trimData?.startTime ?? "") +
      (trimData?.endTime ?? ""),
    [videoSrc, shouldAutoPlay, hasControls, settings.loop, settings.shape,
      cropData?.coordinates?.left, cropData?.coordinates?.top,
      cropData?.coordinates?.width, cropData?.coordinates?.height,
      trimData?.startTime, trimData?.endTime],
  );

  // ===== REF CALLBACKS =====
  const setWrapperRef = useCallback(
    (r: HTMLDivElement | null) => {
      if (!r) return;
      const elementRef = editorData?.elementRef;
      if (elementRef) elementRef.current = r;
      setObserverRef(r);
    },
    [editorData, setObserverRef],
  );

  const setVideoContainerRef = useCallback(
    (r: HTMLDivElement | null) => {
      videoContainerRef.current = r;
      const videoContainerEditorRef = editorData?.videoContainerRef;
      if (videoContainerEditorRef) {
        videoContainerEditorRef.current = r;
      }
    },
    [editorData?.videoContainerRef],
  );

  // ===== RENDER TYPE =====
  const renderType = useMemo((): VideoRenderType => {
    // Preview takes precedence
    if ((isPdf || settings.view === "preview") && settings.type !== "Library") {
      return VideoRenderType.PREVIEW;
    }

    // Library video
    if (settings.type === "Library" && videoSrc.length > 24) {
      return VideoRenderType.LIBRARY_VIDEO;
    }

    // Other video types
    if (settings.type === "Youtube") {
      return VideoRenderType.YOUTUBE;
    }

    if (settings.type === VideoType.Vimeo && settings.src.includes("vimeo")) {
      return VideoRenderType.VIMEO;
    }

    if (settings.type === VideoType.Loom && settings.src.includes("loom")) {
      return VideoRenderType.LOOM;
    }

    if (
      settings.type === VideoType.Vidyard &&
      settings.src.includes("vidyard")
    ) {
      return VideoRenderType.VIDYARD;
    }

    if (settings.type === "Code" && settings.code && settings.code.length) {
      return VideoRenderType.CODE;
    }

    return VideoRenderType.NONE;
  }, [
    isPdf,
    settings.view,
    settings.type,
    settings.src,
    settings.code,
    videoSrc,
  ]);

  // ===== VIDYARD EFFECT =====
  useEffect(() => {
    if (!videoContainerRef.current) return;

    if (
      settings.type === VideoType.Vidyard &&
      settings.src &&
      settings.src.includes("vidyard")
    ) {
      const id = getVidyardVideoId(settings.src);
      if (!id) return;

      const containerEl =
        videoContainerRef.current.querySelector(`.vidyard-wrapper`);
      if (containerEl) {
        (async () => {
          try {
            await VidyardEmbed.api.renderPlayer({
              uuid: id,
              container: containerEl,
              type: "inline",
              autoplay: shouldAutoPlay ? 1 : 0,
              loop: settings.loop ? 1 : 0,
              hidden_controls: hasControls ? 0 : 1,
              muted: 1,
            });
          } catch (e) {
            console.log(e);
          }
        })();

        return () => {
          const player = VidyardEmbed.api.getPlayersByUUID(id)[0];
          if (player) VidyardEmbed.api.destroyPlayer(player);
        };
      }
    }
  }, [settings.type, settings.src, hasControls, shouldAutoPlay, settings.loop]);

  // ===== RENDER FUNCTIONS =====
  const renderLibraryVideo = () => {
    // In PDF mode, if a poster image was uploaded (user-selected thumbnail,
    // already cropped/transformed), render a lightweight <img> instead of
    // loading the full video file.
    if (isPdf && settings.videoPosterUrl) {
      return (
        <PdfLibVideoWrapper href={settings.src}>
          <img
            src={settings.videoPosterUrl}
            alt={settings.ariaLabel || "Video thumbnail"}
            data-has-aspect-ratio={!!metaAspectRatio}
            className="libraryVideo"
          />
        </PdfLibVideoWrapper>
      );
    }

    const videoElement = (
      <StyledVideoElement
        ref={cropFallback?.setLibraryVideoRef}
        width={settings.metadata?.width}
        height={settings.metadata?.height}
        data-has-aspect-ratio={!!metaAspectRatio}
        className="libraryVideo"
        preload={isPdf ? "auto" : undefined}
        key={videoKey}
        autoPlay={shouldAutoPlay}
        playsInline={shouldAutoPlay}
        loop={hasTrim ? false : settings.loop}
        muted={shouldAutoPlay}
        $round={settings.round}
        $blobType={settings.blobType}
        $parallelogramDirection={settings.parallelogramDirection}
        $shape={settings.shape}
        controls={hasControls}
        style={cropVideoStyle}
        poster={settings.videoPosterUrl || undefined}
        {...(settings.ariaLabel ? { "aria-label": settings.ariaLabel } : {})}
      >
        <Source
          url={videoSrc}
          trimStart={hasTrim ? cropFallback?.trimStartTime : undefined}
          trimEnd={hasTrim ? cropFallback?.trimEndTime : undefined}
        />
      </StyledVideoElement>
    );

    return (
      <PdfLibVideoWrapper href={settings.src}>
        {videoSrc && isVisible && (
          cropContainerStyle ? (
            <div style={cropContainerStyle}>{videoElement}</div>
          ) : (
            videoElement
          )
        )}
      </PdfLibVideoWrapper>
    );
  };

  const renderYoutube = () => (
    <div
      className={cn(
        "iframeWrapper",
        isEditor && "tw-pointer-events-none",
        !settings.src && "tw-hidden",
      )}
      key={settings.src}
    >
      <iframe
        src={isVisible ? getYoutubeEmbedSrc(settings.src, settings, isPdf) : ""}
        title="YouTube video player"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="tw-border-none tw-bg-black"
      ></iframe>
    </div>
  );

  const renderVimeo = () => (
    <div
      className={cn("iframeWrapper", isEditor && "tw-pointer-events-none")}
      key={settings.src}
    >
      <iframe
        src={isVisible ? getVimeoEmbedSrc(settings.src, settings, isPdf) : ""}
        title="Vimeo video player"
        allow="autoplay; fullscreen"
        allowFullScreen
        className="tw-border-none tw-bg-black"
      ></iframe>
    </div>
  );

  const renderLoom = () => (
    <PdfLibVideoWrapper href={settings.src}>
      <div
        className={cn("iframeWrapper", isEditor && "tw-pointer-events-none")}
        data-video-type={"loom"}
        key={settings.src}
      >
        <iframe
          src={isVisible ? getLoomEmbedSrc(settings.src, settings, isPdf) : ""}
          title="Loom video player"
          allowFullScreen
          className="tw-border-none tw-bg-black"
        ></iframe>
      </div>
    </PdfLibVideoWrapper>
  );

  const renderVidyard = () => (
    <div
      className={cn(
        "iframeWrapper vidyard-wrapper",
        isEditor && "tw-pointer-events-none",
      )}
      key={settings.src}
    ></div>
  );

  const renderCode = () => (
    <div
      className={cn("iframeWrapper", isEditor && "tw-pointer-events-none")}
      key={settings.code}
    >
      <SandboxedHtml
        code={settings.code}
        title="Embedded video"
        className="embeddedCode"
      />
    </div>
  );

  const renderVideoContent = () => {
    switch (renderType) {
      case VideoRenderType.PREVIEW:
        return <Preview entity={entity} isVisible={isVisible} />;
      case VideoRenderType.LIBRARY_VIDEO:
        return renderLibraryVideo();
      case VideoRenderType.YOUTUBE:
        return renderYoutube();
      case VideoRenderType.VIMEO:
        return renderVimeo();
      case VideoRenderType.LOOM:
        return renderLoom();
      case VideoRenderType.VIDYARD:
        return renderVidyard();
      case VideoRenderType.CODE:
        return renderCode();
      case VideoRenderType.NONE:
      default:
        return null;
    }
  };

  return (
    <StyledVideoWrapper
      className={cn(
        "videoWrapper tw-relative tw-flex",
        {
          "tw-outline-dashed tw-outline-1 tw-outline-[#4CBA78]":
            !!editorData?.isAdmin && !!settings.AIClassificationAnchor,
          "--element-preview-layout":
            (settings.view === "preview" || isPdf) &&
            settings.type !== "Library",
        },
      )}
      $verticalAlignment={settings.verticalAlignment}
      $horizontalAlignment={settings.horizontalAlignment}
      $hasDynamicVariable={hasDynamicVariable}
      data-element-shape={settings.shape || "free"}
      ref={setWrapperRef}
    >
      {editorSlots?.resizeSlot}
      <StyledVideoLayout
        {...DND_ATTR}
        onDoubleClick={editorData?.handleMediaChange}
        className={cn(
          "videoLayout tw-relative tw-h-auto tw-max-w-full max-md:tw-w-full",
          {
            noVideo: !!editorData?.placeholder,
            "max-md:tw-w-1/2": !!width && Number(width) < 50,
          },
        )}
        $round={settings.round}
        $height={height}
        $shadow={settings.shadow}
        $shadowColor={settings.shadowColor}
        $shadowOpacity={settings.shadowOpacity}
        $shape={settings.shape ?? "free"}
        $isShort={settings.isShort}
        $preview={settings.view === "preview"}
        $blobType={settings.blobType}
        $parallelogramDirection={settings.parallelogramDirection}
        $overlayOpacity={settings.overlayOpacity}
        $overlayColor={settings.overlayColor}
        $filter={settings.blackAndWhite}
        style={wrapperStyle}
        ref={setVideoContainerRef}
      >
        {editorSlots?.dynamicVariablePlateSlot}
        {editorSlots?.dynamicVariableDotSlot}
        <div
          className={cn("video__container", {
            "!tw-p-0":
              (settings.view === "preview" || isPdf) &&
              settings.type !== "Library",
            "after:tw-pointer-events-none after:tw-absolute after:tw-inset-0 after:tw-size-full after:tw-[background:var(--overlay-color)] after:tw-content-['']":
              !!settings.overlay && settings.type === "Library",
          })}
          data-video-shape={settings.shape ?? "free"}
          style={containerStyle}
        >
          {renderVideoContent()}
          {settings?.view !== "preview" && editorSlots?.placeholderSlot}
        </div>
      </StyledVideoLayout>
    </StyledVideoWrapper>
  );
};
